import { Log } from '@apparel/core/log';
import { useMounted } from '@hooks/useMounted';
import { ReturnOnlyParams } from '@lib/classes/ReturnOnly';
import { sha1 } from '@lib/hash';
import { http } from '@lib/http';
import { ModelClassType } from '@model/Model';
import { modelName } from '@model/utils/modelName';
import { createCache } from '@packages/cache';
import { CacheDriver } from '@packages/cache/lib/cacheStore';
import { createEvent } from '@packages/events/lib/createEvent';
import { call } from '@packages/functions';
import Axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import { toLower, unset } from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';
import { UseApiRenderProps } from './ApiRender';

export interface PaginationResponse {
    total: number;
    pages: number;
    cursor: {
        next: number;
        current: number;
        previous: number;
    };
}

export type MethodType =
    | string
    | (
          | 'index'
          | 'list'
          | 'all'
          | 'show'
          | 'find'
          | 'update'
          | 'edit'
          | 'destroy'
          | 'remove'
          | 'create'
          | 'save'
          | 'store'
          | 'get'
          | 'post'
          | 'put'
          | 'delete'
          | 'head'
          | 'patch'
          | 'options'
      );

export type RequestParams<T> = T &
    PaginationResponse &
    ReturnOnlyParams & {
        order_by: string;
        order_direction: 'asc' | 'desc';
        offset: number;
        limit: number;
        [key: string]: any;
    };

export interface CacheConfig<T = any> {
    key: string;
    timeout?: number;
    transform?: (data: T) => T;
}

export interface CRUDConfig<T = any> {
    cache?: string | boolean | CacheConfig<T>;
    model?: ModelClassType;
    url?: string;
    data?: Partial<T>;
    params?: Partial<RequestParams<T>>;
    config?: AxiosRequestConfig;
    expandId?: any;
    customComponent?: any;
    FilterComponent?: any;
    openQuestion?: boolean;
    closeQuestion?: any;
}

export interface RequestConfig<T = any> extends CRUDConfig<T> {
    id?: number;
    method?: MethodType | string;
    before?: (config: any) => void | RequestConfig<T>;
    after?: (data: any, res: any) => void;
    onError?: (error: any) => void;
    onLoading?: (state: boolean, cached: boolean) => void;
}

export interface IndexItems<T> {
    items: T[];
    pagination: PaginationResponse;
}

export type ApiResponse<T> = Promise<AxiosResponse<T>>;
export type ApiIndexResponse<T> = ApiResponse<IndexItems<T> | T[]>;

export interface UseApiResponse<Res = any> {
    pagination: PaginationResponse;
    data: Res;
    raw: AxiosResponse<Res>;
    loading: boolean;
    error: Error;
    firstLoad: boolean;
    isEmpty: boolean;
    requests: number;
    statusCode: number;
    statusText: string;
    options: UseApiRenderProps & {
        url?: string;
        model?: ModelClassType;
        debug?: boolean;

        request?: RequestConfig<Res> | ((api: UseApiResponse<Res>) => void);
        onMount?: RequestConfig<Res> | ((api: UseApiResponse<Res>) => void);
        onUnmount?: RequestConfig<Res> | ((api: UseApiResponse<Res>) => void);
    };

    /**
     * request api
     * @param config
     */
    request(config: RequestConfig<Res>): ApiIndexResponse<Res>;

    /**
     * index request
     * @param config
     */
    index(config?: CRUDConfig<Res>): ApiResponse<Res[]>;

    /**
     * save request
     * @param config
     */
    save(config?: CRUDConfig<Res>): ApiResponse<Res>;

    /**
     * show request
     * @param id
     * @param config
     */
    show(id: number, config?: CRUDConfig<Res>): ApiResponse<Res>;

    /**
     *
     * @param config
     */
    get(config?: CRUDConfig<Res>): ApiResponse<Res>;

    /**
     * update request
     * @param id
     * @param config
     */
    update(id: number, config?: CRUDConfig<Res>): ApiResponse<Res>;

    /**
     * destroy request
     * @param id
     * @param config
     */
    destroy(id: number, config?: CRUDConfig<Res>): ApiResponse<Res>;

    /**
     * cancel method
     * @param message
     */
    cancel(message?: string): void;

    /**
     * flush method
     */
    flush(): void;

    /**
     * redo method
     */
    redo(config?: RequestConfig<Res>);

    /**
     * set loading
     * @param loading boolean
     */
    setLoading(loading: boolean): void;

    hasItems(): boolean;

    setData(data: Res | ((prevState: Res) => Res)): void;

    /**
     * fireRequest method
     */
    fireRequest(): ApiResponse<Res>;
}

export interface UseApiOptions<T> extends UseApiRenderProps {
    navigation?: string | null;
    url?: string;
    model?: ModelClassType;
    debug?: boolean;
    noResizeEvent?: boolean;

    request?: RequestConfig<T> | ((api: UseApiResponse<T>) => void);
    onMount?: RequestConfig<T> | ((api: UseApiResponse<T>) => void);
    onUnmount?: RequestConfig<T> | ((api: UseApiResponse<T>) => void);
    onError?: (error: Error | AxiosError) => void;
}

const HTTP_METHOD_ALIAS = {
    get: ['index', 'list', 'all', 'show', 'find'],
    put: ['update', 'edit'],
    delete: ['destroy', 'remove'],
    post: ['create', 'save', 'store'],
    head: [],
    patch: [],
    options: [],
};

const getCache = (config: RequestConfig<any>): CacheConfig<any> | null => {
    if (config.cache) {
        if (typeof config.cache === 'boolean') {
            return getCache({
                ...config,
                cache: {
                    key: sha1(config),
                    timeout: null,
                    transform: (data) => data,
                },
            } as any);
        } else if (typeof config.cache === 'string') {
            return getCache({
                ...config,
                cache: {
                    key: config.cache,
                    timeout: null,
                    transform: (data) => data,
                },
            } as any);
        }

        return config.cache;
    }

    return null;
};

/**
 * Get http method of a CRUD method
 * @param method
 * @returns {string|null}
 */
const getHttpMethod = (method: MethodType) => {
    method = toLower(method) as any;

    if (HTTP_METHOD_ALIAS.hasOwnProperty(method)) {
        return method;
    }

    return Object.keys(HTTP_METHOD_ALIAS).filter((httpMethod) => HTTP_METHOD_ALIAS[httpMethod].includes(method))[0];
};

/**
 * Get config data
 * @param config$
 * @returns {{}}
 */
const getConfigData = (config$) => {
    const data = { ...(config$.config || { data: {} }).data };
    return {
        ...data,
        ...(config$.data || {}),
    };
};

/**
 * Get config params
 * @param config$
 * @returns {{}}
 */
const getConfigParams = (config$) => {
    const params = { ...(config$.config || { params: {} }).params };
    return {
        ...params,
        ...(config$.params || {}),
    };
};

/**
 * Get data & params in same object
 * @param config$
 * @param extend
 * @returns {{}}
 */
const getDataAndParams = (config$: CRUDConfig<any> | RequestConfig<any>, extend: Record<string, any> = {}) => ({
    ...getConfigData(config$),
    ...getConfigParams(config$),
    ...extend,
});

/**
 * Tell us if the method can send body
 * @param method
 * @returns {boolean}
 */
const itCanSendData = (method: MethodType) => /post|put|patch/i.test(method);

const REQUEST_STORE = createCache('http_requests', {
    driver: CacheDriver.SESSION,
    periodicClear: 300,
});

/**
 * Invalidate a cached request
 * @param config
 */
export const invalidateRequestCache = (config: string | RequestConfig) => {
    const cfg = typeof config === 'string' ? { cache: config } : config;
    REQUEST_STORE.destroy(getCache(cfg)!.key);
};

/**
 * Control Api using react hooks
 *
 * @author Dumitrana Alinus
 * @date 13.05.2019
 * @time 16:44
 *
 * @param options
 */
export function useApi<ResType = any>(options: UseApiOptions<ResType> = {}): UseApiResponse<ResType> {
    const isMounted = useMounted();
    const { model, url } = options;

    const log = useMemo(
        () => Log.channel(`Models/utils.useApi(${model ? modelName(model) : url})`).setActive(!!options.debug),
        [options.debug, model, url],
    );

    // Initialize state
    const [data, setData] = useState<ResType | null>(null);
    const [raw, setRaw] = useState<null | AxiosResponse<ResType>>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<Error | null>(null);
    const [firstLoad, setFirstLoad] = useState<boolean>(true);
    const [isEmpty, setEmpty] = useState<boolean>(true);
    const [requests, setRequests] = useState<number>(0);
    const [statusCode, setStatusCode] = useState<number>(-1);
    const [statusText, setStatusText] = useState<string>('Not set');
    const [navigation, setNavigation] = useState<string | null>(options.navigation || null);
    const [pagination, setPagination] = useState<PaginationResponse | null>(null);

    const lastRequest = useRef<null | RequestConfig<ResType>>(null);

    /**
     * General request function
     * @param config$
     * @returns {ApiResponse<ResType>}
     */
    const request = async function (config$: RequestConfig<ResType> = {}): Promise<AxiosResponse<ResType>> {
        log.debug('Send request', { config: config$ });
        log.debug('is mounted?', isMounted());

        config$ = call(config$.before, config$) || config$;

        const method$ = getHttpMethod(config$.method as any) ?? config$.method;
        const cache$ = getCache(config$);

        // Store last request parameters used in redo method
        lastRequest.current = config$;

        // Construct defaults
        let error$;
        let data$: null | AxiosResponse<ResType> = null;
        const endpoint: ModelClassType | string = (config$.model || config$.url || model || url) as any;

        if (!endpoint) {
            throw new Error('No method | url defined!');
        }

        // Set loading state
        if (isMounted()) {
            log.debug('Set requests', requests + 1);
            setRequests(requests + 1);
            setLoading(true);
            setError(null);
            call(config$.onLoading, true, false);
        }

        // Set data from cache
        if (cache$ && cache$.key && isMounted()) {
            const reqData = REQUEST_STORE.get(cache$.key);

            if (reqData) {
                const ks = Object.keys(reqData);
                const hasData = ks.length > 0;
                const hasItems =
                    ks.includes('items') && ks.includes('pagination') && reqData.items && reqData.items.length > 0;

                if (isMounted() && ((hasData && hasItems) || (hasData && !hasItems))) {
                    call(config$.after, reqData, {
                        data: reqData,
                        cached: true,
                    });

                    setData(reqData);

                    call(config$.onLoading, false, true);
                    setLoading(false);
                }
            }
        }

        // Pagination params
        // const requestParams = {
        //     limit:           pagination.limit,
        //     offset:          pagination.offset,
        //     page:            pagination.cursor.current,
        //     order_by:        pagination.order_by,
        //     order_direction: pagination.order_direction
        // };

        const requestParams: { [key: string]: any } = {};

        if (config$.method === 'all') {
            requestParams.all = 'true';
        }

        try {
            if (typeof endpoint !== 'string') {
                config$.params = { ...requestParams, ...config$.params } as any;
                // Map data on the right methods if the endpoint it's a model
                switch (method$) {
                    case 'post':
                        data$ = await (endpoint as any).create(config$.data, config$.params, config$.config);
                        break;

                    case 'put':
                        data$ = await (endpoint as any).update(
                            config$.id,
                            config$.data,
                            config$.params,
                            config$.config,
                        );
                        break;

                    case 'delete':
                        data$ = await (endpoint as any).destroy(
                            config$.id,
                            config$.data,
                            config$.params,
                            config$.config,
                        );
                        break;

                    default:
                        const params$ = getDataAndParams(config$);
                        if (typeof endpoint[method$] === 'function') {
                            data$ = await endpoint[method$](params$, config$.config);
                        } else {
                            if (config$.id) {
                                data$ = await endpoint.find(config$.id, params$, config$.config);
                            } else {
                                data$ = await endpoint.index(params$, config$.config);
                            }
                        }

                        break;
                }
            } else {
                const requestConfig: AxiosRequestConfig = {
                    ...config$.config,
                };

                requestConfig.params = {
                    id: config$.id,
                    ...requestConfig.params,
                    ...(!itCanSendData(method$ as any) ? getDataAndParams(config$) : getConfigParams(config$)),
                };

                requestConfig.data = getConfigData(config$);

                log.debug('Send plain HTTP', { endpoint, config: requestConfig });

                const args$ = [requestConfig];

                if (itCanSendData(method$ as any)) {
                    args$.unshift({ ...requestConfig.data });
                    unset(requestConfig, 'data');
                }

                // Send axios request
                data$ = await http[method$]<ResType>(endpoint, ...args$);
            }

            // cache the request
            if (cache$ && cache$.key && data$ && data$.data) {
                const { data } = { ...data$ };

                REQUEST_STORE.put(
                    cache$.key,
                    typeof cache$.transform === 'function' ? cache$.transform(data) : data,
                    cache$.timeout,
                );
            }
            if (isMounted()) {
                call(config$.after, data$ ? data$.data : null, data$);
            }
        } catch (e) {
            log.debug('Catch error', { error: e });
            error$ = e;
            data$ = e.response || null;

            if (Axios.isCancel(e)) {
                log.debug('@req.canceled: %s', e.message);
            } else {
                // Set error if there's a non-handled issue.
                setError(e);
            }

            call(options.onError, e);
            call(config$.onError, e);
        }

        if (isMounted()) {
            call(config$.onLoading, false, false);
            log.debug('Set data');

            setRaw(data$); // Update raw       in state
            setData(data$ ? data$.data : null); // Update data      in state
            setLoading(false); // Update loading   in state
            setFirstLoad(false); // Update firstLoad in state
            setPagination(data$ && data$.data ? (data$.data as any).pagination : null);

            if (data$) {
                setStatusCode(data$.status);
                setStatusText(data$.statusText);
            }

            if (method$ === 'get' && !config$.id) {
                setEmpty(!data$ || !data$.data || !(data$.data as any).items || (data$.data as any).items.length === 0);
            } else {
                setEmpty(!data$ || !data$.data);
            }

            if (typeof window !== 'undefined' && !options.noResizeEvent) {
                setTimeout(() => {
                    window.dispatchEvent(createEvent('resize'));
                });
            }

            if (error$) {
                throw error$;
            }
        }

        log.debug('Finish', { data: data$ });

        return data$ as any;
    };

    /**
     * Cancel method
     * @param message
     */
    const cancel = (message?: string) => {
        if (!loading) {
            return;
        }
        // Todo: implement;
        log.debug(`[Implement] Canceled!\n    Reason: ${message || ''}`);
    };

    /**
     * Create index request
     * @param config$
     * @returns {ApiResponse<ResType[]>}
     */
    const index = (config$?: CRUDConfig<ResType>): Promise<IndexItems<ResType> | ResType[]> =>
        request({ ...(config$ as any), method: 'index' }) as any;

    /**
     * Create store request
     * @param config$
     * @returns {ApiResponse<ResType>}
     */
    const save = (config$?: CRUDConfig<ResType>): ApiResponse<ResType> =>
        request({ ...(config$ as any), method: 'store' });

    /**
     *
     * @param config$
     */
    const get = (config$?: CRUDConfig<ResType>): ApiResponse<ResType> =>
        request({ ...(config$ as any), method: 'get' });

    /**
     * Create show request
     * @param id
     * @param config$
     * @returns {ApiResponse<ResType>}
     */
    const show = (id: number, config$?: CRUDConfig<ResType>): ApiResponse<ResType> =>
        request({ ...(config$ as any), method: 'show', id });

    /**
     *
     * @param id
     * @param config$
     * @returns {ApiResponse<ResType>}
     */
    const update = (id: number, config$?: CRUDConfig<ResType>): ApiResponse<ResType> =>
        request({ ...(config$ as any), method: 'update', id });

    /**
     *
     * @param id
     * @param config$
     * @returns {ApiResponse<ResType>}
     */
    const destroy = (id: number, config$?: CRUDConfig<ResType>): ApiResponse<ResType> =>
        request({ ...(config$ as any), method: 'delete', id });

    /**
     * Clear request
     */
    const flush = (): void => {
        lastRequest.current = null;
        setFirstLoad(true);
        setData(null);
        setRaw(null);
        setLoading(false);
        setError(null);
        setRequests(0);
        setStatusCode(-1);
        setEmpty(true);
        setPagination({} as any);
    };

    /**
     * Redo last request using new params
     * @returns {ApiResponse<ResType>}
     * @param config
     */
    const redo = async function (config?: RequestConfig<ResType>): Promise<AxiosResponse<ResType | null>> {
        log.debug('Fire redo request', lastRequest.current);

        if (config || lastRequest.current) {
            return await request((config || lastRequest.current) as any);
        }

        return {} as any;
    };

    // /**
    //  * Set next page to pagination and redo the request
    //  * @param config
    //  * @returns {ApiResponse<ResType>}
    //  */
    // const fetchNext = async function ( config?: RequestConfig ): ApiResponse<ResType> {
    //     pagination.nextPage();
    //     return await redo<ResType>(config);
    // };
    //
    // /**
    //  * Set previous page to pagination and redo the request
    //  * @param config
    //  * @returns {ApiResponse<ResType>}
    //  */
    // const fetchPrevious = async function ( config?: RequestConfig ): ApiResponse<ResType> {
    //     pagination.previousPage();
    //     return await redo<ResType>(config);
    // };
    //
    // /**
    //  * Set page to pagination and redo the request
    //  * @param page$
    //  * @param config
    //  * @returns {ApiResponse<ResType>}
    //  */
    // const fetchPage = async function ( page$: number, config?: RequestConfig ): ApiResponse<ResType> {
    //     pagination.setPage(page$);
    //     return await redo<ResType>(config);
    // };

    /**
     * Fire request
     * @returns {ApiResponse<ResType>}
     */
    const fireRequest = (): Promise<AxiosResponse<ResType>> => request(options.request as any);

    /**
     * Fire onMount request
     * @returns {ApiResponse<ResType>}
     */
    const fireMountRequest = (): Promise<AxiosResponse<ResType>> =>
        request((typeof options.onMount === 'string' ? { method: options.onMount } : options.onMount) as any);

    /**
     * Fire onUnmount request
     * @returns {ApiResponse<ResType>}
     */
    const fireUnmountRequest = (): Promise<AxiosResponse<ResType>> =>
        request((typeof options.onUnmount === 'string' ? { method: options.onUnmount } : options.onUnmount) as any);

    const hasItems = (): boolean =>
        !!data && ((Array.isArray(data) && data.length > 0) || ((data as any).items && (data as any).items.length > 0));

    const api$response: UseApiResponse<ResType> = {
        data,
        raw,
        pagination,
        loading,
        setLoading,
        error,
        firstLoad,
        requests,
        options,

        isEmpty,
        statusCode,
        statusText,

        // API Methods
        request,
        index,
        save,
        get,
        show,
        update,
        destroy,

        // Pagination
        // fetchNext,
        // fetchPrevious,
        // fetchPage,

        // Helpers
        cancel,
        flush,
        redo,
        fireRequest,
        setData,
        setRaw,

        hasItems,
    } as any;

    // Control the mount effect
    useEffect(() => {
        log.debug('@effect.mount');

        if (options.onMount) {
            log.debug('Fire mount response');
            if (typeof options.onMount === 'function') {
                options.onMount(api$response);
            } else {
                // noinspection SuspiciousTypeOfGuard
                if (typeof options.onMount === 'object' || typeof options.onMount === 'string') {
                    // noinspection JSIgnoredPromiseFromCall
                    fireMountRequest();
                }
            }
        }
    }, [((options.onMount as any) || { id: '' }).id]);

    useEffect(() => {
        return () => {
            (async () => {
                log.debug('@effect.unmount');

                // TODO: Cancel request
                cancel('Unmount event');

                if (options.onUnmount) {
                    if (typeof options.onUnmount === 'function') {
                        await options.onUnmount(api$response);
                    } else {
                        // noinspection SuspiciousTypeOfGuard
                        if (typeof options.onUnmount === 'object' || typeof options.onUnmount === 'string') {
                            await fireUnmountRequest();
                        }
                    }
                }
            })();
        };
    }, [((options.onUnmount as any) || { id: '' }).id]);

    /**
     * For tab navigation pass navigation type to recall the api.
     */
    if (navigation && navigation !== options.navigation) {
        if (isMounted()) {
            setNavigation(options.navigation as any);
        }

        // noinspection JSIgnoredPromiseFromCall
        fireMountRequest();
    }

    return api$response;
}
